import styled from 'styled-components6';
import {
    FontAwesomeIcon,
    FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import {
    ChangeEvent,
    FocusEvent,
    KeyboardEvent,
    MouseEvent,
    useEffect,
    useRef,
    useState,
} from 'react';
import { When } from 'react-if';
import { ButtonBase } from '../../theme';

const InputAndButtonsContainer = styled.div<{ $isActive: boolean }>`
    display: flex;
    width: 100%;
    border-radius: var(--rwl-radius-sm);
    border: 2px solid ${({ $isActive }) => ($isActive ? '#000' : 'transparent')};
    transition: border 0.2s ease;
`;

const INPUT_HEIGHT = 38;

const InputWrapper = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'smallSize' && prop !== 'variant', //remove console warning of invalid prop passed to HTML element
})<{ smallSize: boolean; variant: 'light' | 'dark' }>`
    height: ${INPUT_HEIGHT}px;
    position: relative;
    font-size: 1em;
    line-height: 150%;
    font-weight: normal;
    padding: 10px 14px;
    display: flex;
    gap: var(--rwl-spacing-sm);
    align-items: center;
    align-self: stretch;
    max-width: ${(props) => (props.smallSize ? '35em' : 'unset')};
    border-top-left-radius: var(--rwl-radius-sm);
    border-bottom-left-radius: var(--rwl-radius-sm);
    background: ${(props) =>
        props.variant === 'light'
            ? 'var(--rwl-palette-white)'
            : 'var(--rwl-palette-gray-100)'};
    transition: background 0.2s ease;

    &.active {
        background: var(--rwl-palette-white);
    }

    &:hover {
        background: ${(props) =>
            props.variant === 'light'
                ? 'var(--rwl-palette-white)'
                : 'var(--rwl-palette-gray-200)'};
    }

    z-index: 20;
    flex: 1;
`;

const Input = styled.input`
    border: none;
    outline: none;
    flex: 1;
    font-size: 1em;
    line-height: 150%;
    font-weight: normal;
    padding: 0;
    background: transparent;
    min-width: 0;
    color: var(--rwl-text-default);

    &::placeholder {
        color: var(--rwl-text-hint);
    }
`;

const AutocompleteWrapper = styled.div`
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    border-radius: 8px;
    background: var(--rwl-palette-white, #fff);
    box-shadow: 0 3px 5px -1px rgba(0, 0, 0, 0.2),
        0 6px 10px 0 rgba(0, 0, 0, 0.14), 0 1px 18px 0 rgba(0, 0, 0, 0.12);
`;

const AutocompleteItem = styled.div`
    display: flex;
    padding: 10px 14px;
    align-items: center;
    gap: 8px;
    align-self: stretch;
    cursor: pointer;
    color: var(--rwl-text-default);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:focus,
    &.active {
        background: var(--rwl-palette-gray-100);
    }
`;

const AutocompleteButtonBase = styled(ButtonBase).withConfig({
    shouldForwardProp: (prop) => prop !== 'parentIsHovered', // remove console warning of invalid prop passed to HTML element
})<{ parentIsHovered: boolean; variant: 'light' | 'dark' }>`
    align-items: center;
    background: ${({ parentIsHovered, variant }) =>
        variant === 'dark'
            ? `var(--rwl-palette-gray-${parentIsHovered ? '200' : '100'})`
            : 'var(--rwl-palette-white)'};
    height: ${INPUT_HEIGHT}px;
    transition: background 0.2s ease;

    &:hover {
        background: ${({ variant }) =>
            variant === 'dark'
                ? `var(--rwl-palette-gray-200)`
                : 'var(--rwl-palette-white)'};
    }

    // icons
    svg {
        color: var(--rwl-palette-gray-500);
    }

    // buttons
    a {
        padding: var(--rwl-spacing-sm);
    }
`;

export enum AutocompleteTypes {
    CURRENT = 'current',
    RECENT = 'recent',
    SUGGESTION = 'suggestion',
}

export type AutocompleteOption = {
    type: AutocompleteTypes;
    icon?: FontAwesomeIconProps['icon'];
    key: string;
    label: string;
    onSelected?: () => void;
};

type TypeAheadInputProps = {
    suggestions: AutocompleteOption[];
    prefilledValue?: string;
    onTyping?: (value: string) => void;
    onClear?: () => void;
    smallSize?: boolean;
    variant?: 'light' | 'dark';
    onFocus?: () => void;
    onBlur?: () => void;
};

export const TypeAheadInput = ({
    suggestions,
    prefilledValue,
    onTyping = () => void 0,
    onClear = () => void 0,
    smallSize = true,
    variant = 'dark',
    onFocus = () => void 0,
    onBlur = () => void 0,
}: TypeAheadInputProps) => {
    const inputEl = useRef<HTMLInputElement>(null);
    const [value, setValue] = useState('');
    const [tempValue, setTempValue] = useState('');
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedOption, setHighlightedOption] =
        useState<AutocompleteOption | null>(null);
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        setValue(prefilledValue ?? '');
        setTempValue(prefilledValue ?? '');
    }, [prefilledValue]);

    useEffect(() => {
        if (isInputFocused) {
            setIsOpen(true);
        }
    }, [isInputFocused]);

    useEffect(() => {
        if (value !== tempValue) {
            setTempValue(value);
        }
    }, [value]);

    useEffect(() => {
        onTyping(tempValue);
    }, [tempValue]);

    useEffect(() => {
        if (!isOpen) {
            setHighlightedOption(null);
            onBlur();
        } else {
            inputEl.current?.focus();
            onFocus();
        }
    }, [isOpen]);

    const onSelectOption = (option: AutocompleteOption) => {
        if (option) {
            setValue(option.label);
            if (option.onSelected) {
                option.onSelected();
            }
        }
        setIsOpen(false);
        setIsHovered(false);
    };

    return (
        <InputAndButtonsContainer $isActive={isOpen}>
            <InputWrapper
                variant={variant}
                smallSize={smallSize}
                onMouseOver={() => setIsHovered(true)}
                onMouseOut={() => setIsHovered(false)}
                onMouseDown={() => setIsOpen(true)}
                onFocus={() => setIsOpen(true)}
                onBlur={() => setIsOpen(false)}
                onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                    e.stopPropagation();
                    if (
                        highlightedOption &&
                        (e.key === 'Enter' || e.key === 'Tab')
                    ) {
                        onSelectOption(highlightedOption);
                        return;
                    }
                    if (
                        !highlightedOption &&
                        (e.key === 'Enter' || e.key === 'Tab')
                    ) {
                        const firstSuggestion = suggestions.find(
                            (s) => s.type === 'suggestion'
                        );
                        if (firstSuggestion) {
                            onSelectOption(firstSuggestion);
                        }
                        return;
                    }
                    if (e.key === 'Escape') {
                        setTempValue(value);
                        inputEl.current?.blur();
                        setIsOpen(false);
                        return;
                    }
                    if (e.key === 'ArrowDown') {
                        const index = suggestions.findIndex((option) =>
                            areOptionsTheSame(option, highlightedOption)
                        );
                        if (index === -1) {
                            setHighlightedOption(suggestions[0]);
                        } else {
                            setHighlightedOption(
                                suggestions[index + 1] ?? null
                            );
                        }
                        return;
                    }
                    if (e.key === 'ArrowUp') {
                        const index = suggestions.findIndex((option) =>
                            areOptionsTheSame(option, highlightedOption)
                        );
                        if (index === -1) {
                            setHighlightedOption(
                                suggestions[suggestions.length - 1]
                            );
                        } else {
                            setHighlightedOption(
                                suggestions[index - 1] ?? null
                            );
                        }
                        return;
                    }
                }}
                className={isOpen ? 'active' : ''}
            >
                <Input
                    ref={inputEl}
                    placeholder={'Address, neighborhood, or zip code'}
                    value={tempValue}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setTempValue(e.target.value)
                    }
                    onFocus={(e: FocusEvent<HTMLInputElement>) => {
                        e.target.select();
                        setIsInputFocused(true);
                    }}
                    onBlur={() => setIsInputFocused(false)}
                    onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                        if (
                            isOpen &&
                            (e.key === 'ArrowDown' || e.key === 'ArrowUp')
                        ) {
                            e.preventDefault();
                        }
                    }}
                />
                <When condition={isOpen}>
                    <AutocompleteWrapper>
                        {suggestions.map((option) => {
                            const { type, key, label, icon } = option;
                            const isActive = areOptionsTheSame(
                                option,
                                highlightedOption
                            );
                            return (
                                <AutocompleteItem
                                    key={`${type}-${key}`}
                                    onMouseDown={(
                                        e: MouseEvent<HTMLDivElement>
                                    ) => {
                                        e.stopPropagation();
                                        onSelectOption(option);
                                    }}
                                    className={isActive ? 'active' : ''}
                                    onMouseMove={() =>
                                        setHighlightedOption(option)
                                    }
                                >
                                    <span
                                        style={{
                                            width: '18px',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <When condition={!!icon}>
                                            <FontAwesomeIcon
                                                icon={icon!}
                                                fontSize={'18px'}
                                            />
                                        </When>
                                    </span>
                                    <span
                                        style={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {label}
                                    </span>
                                </AutocompleteItem>
                            );
                        })}
                    </AutocompleteWrapper>
                </When>
            </InputWrapper>
            <When condition={value?.length || tempValue?.length}>
                <AutocompleteButtonBase
                    parentIsHovered={isHovered}
                    onClick={() => {
                        setValue('');
                        setTempValue('');
                        onClear();
                    }}
                    variant={variant}
                >
                    <FontAwesomeIcon icon={faXmark} fontSize={'18px'} />
                </AutocompleteButtonBase>
            </When>
            <AutocompleteButtonBase
                parentIsHovered={isHovered}
                onClick={() => {
                    if (highlightedOption) {
                        onSelectOption(highlightedOption);
                        return;
                    }
                    const firstSuggestion = suggestions.find(
                        (s) => s.type === 'suggestion'
                    );
                    if (firstSuggestion) {
                        onSelectOption(firstSuggestion);
                    }
                }}
                style={{
                    borderBottomRightRadius: 'var(--rwl-radius-sm)',
                    borderTopRightRadius: 'var(--rwl-radius-sm)',
                }}
                variant={variant}
            >
                <FontAwesomeIcon
                    icon={faSearch}
                    fontSize={'18px'}
                    style={{ cursor: 'pointer' }}
                />
            </AutocompleteButtonBase>
        </InputAndButtonsContainer>
    );
};

function areOptionsTheSame(
    option1: AutocompleteOption | null,
    option2: AutocompleteOption | null
) {
    return option1?.key === option2?.key && option1?.type === option2?.type;
}
